import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Alert,
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ArrowBackRounded from '@mui/icons-material/ArrowBackRounded';
import SendRounded from '@mui/icons-material/SendRounded';
import MailOutlineRounded from '@mui/icons-material/MailOutlineRounded';
import ForumOutlined from '@mui/icons-material/ForumOutlined';
import CheckCircleRounded from '@mui/icons-material/CheckCircleRounded';
import CancelRounded from '@mui/icons-material/CancelRounded';
import RefreshRounded from '@mui/icons-material/RefreshRounded';
import ChatBubbleOutlineRounded from '@mui/icons-material/ChatBubbleOutlineRounded';
import Swal from 'sweetalert2';
import { io, Socket } from 'socket.io-client';
import { useNavigate } from 'react-router-dom';
import {
  acceptChatRequest,
  declineChatRequest,
  getChatRequests,
  sendChatRequest,
} from '../services/api';
import { getToken } from '../utils/auth';

type SlimUser = {
  _id: string;
  fullName?: string;
  email?: string;
  username?: string;
};

type ChatThread = {
  _id: string;
  requestedBy: SlimUser;
  requestedTo: SlimUser;
  status: 'pending' | 'active' | 'declined';
};

type RequestLists = {
  incoming: ChatThread[];
  outgoing: ChatThread[];
};

const displayName = (u?: SlimUser | null) =>
  u?.fullName || u?.username || u?.email || 'Unknown user';

const FriendRequests = () => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [requests, setRequests] = useState<RequestLists>({ incoming: [], outgoing: [] });
  const [loadingRequests, setLoadingRequests] = useState(true);
  const [sendingRequest, setSendingRequest] = useState(false);
  const socketRef = useRef<Socket | null>(null);

  const loadRequests = useCallback(async () => {
    setLoadingRequests(true);
    try {
      const data = await getChatRequests();
      setRequests({
        incoming: data.incoming || [],
        outgoing: data.outgoing || [],
      });
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Could not load requests', 'error');
    } finally {
      setLoadingRequests(false);
    }
  }, []);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  // Real-time: refresh when a request comes in or is updated
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const base = (process.env.REACT_APP_BASE_URL || 'http://localhost:4000/api').replace(/\/api$/, '');
    const socket = io(base, {
      transports: ['websocket', 'polling'],
      auth: { token },
      reconnectionAttempts: 5,
      withCredentials: true,
    });
    socketRef.current = socket;
    socket.on('chat:request', loadRequests);
    socket.on('chat:request:update', loadRequests);
    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [loadRequests]);

  const handleSendRequest = async () => {
    if (!email.trim()) {
      Swal.fire('Missing email', 'Enter an email address to send a request.', 'warning');
      return;
    }
    setSendingRequest(true);
    try {
      const res = await sendChatRequest(email.trim());
      await loadRequests();
      Swal.fire('Request sent', res?.message || 'Friend request sent.', 'success');
      setEmail('');
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Could not send request', 'error');
    } finally {
      setSendingRequest(false);
    }
  };

  const handleAccept = async (id: string) => {
    try {
      const res = await acceptChatRequest(id);
      await loadRequests();
      Swal.fire('Accepted', res?.message || 'Friend request accepted.', 'success').then(() => {
        navigate('/chat');
      });
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Could not accept request', 'error');
    }
  };

  const handleDecline = async (id: string) => {
    try {
      const res = await declineChatRequest(id);
      await loadRequests();
      Swal.fire('Declined', res?.message || 'Friend request declined.', 'info');
    } catch (err: any) {
      Swal.fire('Error', err?.response?.data?.message || 'Could not decline request', 'error');
    }
  };

  const totalPending = requests.incoming.length + requests.outgoing.length;

  return (
    <Stack spacing={3}>
      {/* Header */}
      <Stack direction="row" alignItems="center" spacing={1.5}>
        <Tooltip title="Back to Chat">
          <IconButton onClick={() => navigate('/chat')} size="small">
            <ArrowBackRounded />
          </IconButton>
        </Tooltip>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h4" fontWeight={800}>
            Friend Requests
          </Typography>
          <Typography color="text.secondary">
            Send requests by email and manage incoming ones here.
          </Typography>
        </Box>
        <Tooltip title="Refresh">
          <span>
            <IconButton onClick={loadRequests} disabled={loadingRequests}>
              {loadingRequests ? <CircularProgress size={18} /> : <RefreshRounded fontSize="small" />}
            </IconButton>
          </span>
        </Tooltip>
        {totalPending > 0 && (
          <Chip size="small" label={`${totalPending} pending`} color="warning" />
        )}
      </Stack>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={3}>
        {/* ===== Send Friend Request ===== */}
        <Paper sx={{ p: 3, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <MailOutlineRounded color="primary" />
            <Typography variant="h6" fontWeight={700}>Send Friend Request</Typography>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Friend's email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="example@email.com"
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSendRequest();
              }}
            />
            <Button
              variant="contained"
              startIcon={
                sendingRequest
                  ? <CircularProgress size={18} color="inherit" />
                  : <SendRounded />
              }
              onClick={handleSendRequest}
              disabled={sendingRequest}
              sx={{ whiteSpace: 'nowrap' }}
            >
              Send
            </Button>
          </Stack>
          <Alert sx={{ mt: 2 }} severity="info" icon={<ChatBubbleOutlineRounded fontSize="small" />}>
            Use the email linked to your teammate&apos;s account. Once they accept, you can start chatting.
          </Alert>
        </Paper>

        {/* ===== Incoming & Sent Requests ===== */}
        <Paper sx={{ p: 3, flex: 1 }}>
          <Stack direction="row" spacing={1} alignItems="center" mb={2}>
            <ForumOutlined color="primary" />
            <Typography variant="h6" fontWeight={700}>Requests</Typography>
          </Stack>

          <Stack spacing={1.5}>
            {/* Incoming */}
            <Typography variant="subtitle2" color="text.secondary">
              Incoming
              {!loadingRequests && requests.incoming.length > 0 && (
                <Chip
                  size="small"
                  label={requests.incoming.length}
                  color="primary"
                  sx={{ ml: 1, height: 18, fontSize: 11 }}
                />
              )}
            </Typography>

            {loadingRequests ? (
              Array.from({ length: 2 }).map((_, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="65%" />
                      <Skeleton width="40%" />
                    </Box>
                    <Skeleton variant="rounded" width={32} height={32} />
                    <Skeleton variant="rounded" width={32} height={32} />
                  </Stack>
                </Paper>
              ))
            ) : requests.incoming.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No pending incoming requests.
              </Typography>
            ) : (
              requests.incoming.map((req) => (
                <Paper variant="outlined" key={req._id} sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 36, height: 36 }}>
                      {(displayName(req.requestedBy).charAt(0) || 'U').toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700}>{displayName(req.requestedBy)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {req.requestedBy?.email}
                      </Typography>
                    </Box>
                    <Tooltip title="Accept">
                      <IconButton color="primary" onClick={() => handleAccept(req._id)}>
                        <CheckCircleRounded />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Decline">
                      <IconButton color="error" onClick={() => handleDecline(req._id)}>
                        <CancelRounded />
                      </IconButton>
                    </Tooltip>
                  </Stack>
                </Paper>
              ))
            )}

            <Divider sx={{ my: 1 }} />

            {/* Sent / Outgoing */}
            <Typography variant="subtitle2" color="text.secondary">
              Sent
              {!loadingRequests && requests.outgoing.length > 0 && (
                <Chip
                  size="small"
                  label={requests.outgoing.length}
                  color="warning"
                  sx={{ ml: 1, height: 18, fontSize: 11 }}
                />
              )}
            </Typography>

            {loadingRequests ? (
              Array.from({ length: 2 }).map((_, idx) => (
                <Paper key={idx} variant="outlined" sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Skeleton variant="circular" width={36} height={36} />
                    <Box sx={{ flex: 1 }}>
                      <Skeleton width="65%" />
                      <Skeleton width="40%" />
                    </Box>
                    <Skeleton variant="rounded" width={72} height={24} />
                  </Stack>
                </Paper>
              ))
            ) : requests.outgoing.length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No outgoing requests.
              </Typography>
            ) : (
              requests.outgoing.map((req) => (
                <Paper variant="outlined" key={req._id} sx={{ p: 1.5 }}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Avatar sx={{ width: 36, height: 36 }}>
                      {(displayName(req.requestedTo).charAt(0) || 'U').toUpperCase()}
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography fontWeight={700}>{displayName(req.requestedTo)}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {req.requestedTo?.email}
                      </Typography>
                    </Box>
                    <Chip size="small" label="Pending" color="warning" variant="outlined" />
                  </Stack>
                </Paper>
              ))
            )}
          </Stack>
        </Paper>
      </Stack>
    </Stack>
  );
};

export default FriendRequests;
